import { Repeat } from '@griply/assets';
import {
  getCalendarOccurrenceCheckState,
  getTaskDayCompletionPercentage,
} from '@griply/utils';
import { isSameDay } from 'date-fns';
import React, { useCallback, useMemo, useRef } from 'react';
import { IconFormIcons } from 'shared/assets/icons';
import { TaskCardContextMenu } from 'shared/components/connected/task-card-context-menu';
import { CalendarEventSyncBadge } from 'shared/components/ui/calendar-event-sync-badge';
import { getTaskCheckboxColor } from 'shared/components/ui/checkbox';
import { Icon } from 'shared/components/ui/icon';
import { TaskDetailCheckbox } from 'shared/components/ui/task-detail-checkbox';
import { Tooltip } from 'shared/components/ui/tooltip';
import { tooltipDelay } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { CalendarTaskSync } from 'shared/types/calendar-task-sync';
import { ID } from 'shared/types/id';
import { Task, TaskCheckOptions } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getScheduleFrequencyDayTarget } from 'shared/utils/get-schedule-frequency-day-target';

import * as Styled from './event.style';
import { EventCard } from './event-card';

export type EventTaskProps = {
  task: Task;
  occurrenceId?: ID;
  onComplete: (id: ID, date: Timestamp) => void;
  referenceDate: string;
  weekStartsOn: WeekDays;
  onClick?: (id: ID) => void;
  isDeadline?: boolean;
  showDeadlineFlag?: boolean;
  color?: string;
  numberOfLines: number;
  timeText?: string;
  isPast?: boolean;
  completed?: boolean;
  onInteractionComplete?: () => void;
  calendarSync?: CalendarTaskSync;
};

export const TaskBasicEvent: React.FC<EventTaskProps> = ({
  task,
  occurrenceId,
  referenceDate,
  onComplete,
  weekStartsOn,
  onClick,
  isDeadline,
  showDeadlineFlag,
  color,
  numberOfLines,
  timeText,
  isPast,
  completed,
  onInteractionComplete,
  calendarSync,
}) => {
  const today = useToday();
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();

  const containerRef = useRef<HTMLDivElement>(null);

  const refDate = useMemo(() => new Date(referenceDate), [referenceDate]);

  const _onClick = useCallback(
    (e: React.MouseEvent) => {
      // habits and repeating tasks in the future should open the context menu
      if (task.type !== TaskType.Task && !isSameDay(referenceDate, today)) {
        openMenu(e);
        return;
      }

      onClick?.(task.id);
    },
    [onClick, openMenu, referenceDate, task.id, task.type, today],
  );

  const { checkOption: checked, isFutureRepeating } = useMemo(
    () =>
      getCalendarOccurrenceCheckState(task, refDate, { today, weekStartsOn }),
    [refDate, task, today, weekStartsOn],
  );

  const _onComplete = useCallback(() => {
    if (isFutureRepeating) {
      // do not allow completing habits for future dates
      onClick?.(task.id);
      return;
    }
    onComplete(task.id, refDate);
  }, [isFutureRepeating, onClick, onComplete, refDate, task.id]);

  const completionPercentage = useMemo(
    () =>
      task.schedules?.length
        ? getTaskDayCompletionPercentage(task, {
            day: refDate,
            weekStartsOn,
          })
        : undefined,
    [refDate, task, weekStartsOn],
  );

  const target = useMemo(() => {
    const activeSchedule = getActiveSchedule(task.schedules, today);
    if (!activeSchedule) {
      return;
    }

    return activeSchedule.frequency
      ? getScheduleFrequencyDayTarget(activeSchedule)
      : activeSchedule.completionTarget.count;
  }, [task.schedules, today]);

  const isRepeatingIcon =
    isFutureRepeating ||
    [
      TaskCheckOptions.RepeatSingleChecked,
      TaskCheckOptions.RepeatChecked,
    ].includes(checked);

  const syncState = calendarSync?.states.get(task.id);

  const onToggleSync = () =>
    calendarSync?.onToggleSync(task, !!syncState?.isSynced);

  const onToggleVisibility = () =>
    calendarSync?.onToggleVisibility(task, !!syncState?.isHidden);

  return (
    <>
      <EventCard
        ref={containerRef}
        title={task.name}
        color={color}
        timeText={timeText}
        completed={completed}
        isPast={isPast}
        isDeadline={isDeadline || showDeadlineFlag}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        trailing={
          syncState && (
            <CalendarEventSyncBadge
              isSynced={syncState.isSynced}
              isHidden={syncState.isHidden}
              onToggleSync={onToggleSync}
              onToggleVisibility={onToggleVisibility}
              color={getTaskCheckboxColor(color, task.priority)}
            />
          )
        }
        leading={
          isRepeatingIcon ? (
            <Styled.LeadingSlot $color={color} $past={isPast}>
              <Icon
                icon={task.iconName ? IconFormIcons[task.iconName] : Repeat}
                strokeWidth={2}
              />
            </Styled.LeadingSlot>
          ) : (
            <Styled.MetaContainer>
              <TaskDetailCheckbox
                checked={checked}
                name={task.name}
                completionPercentage={completionPercentage}
                target={target}
                priority={task.priority}
                onChange={_onComplete}
                color={color}
                isFrozen={!!task.frozenAt}
                isSmall
                isFaded={completed || isPast}
              />
            </Styled.MetaContainer>
          )
        }
      />

      {!!menuPosition && (
        <TaskCardContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
          timeslotDate={refDate}
          occurrenceId={occurrenceId}
          onAction={onInteractionComplete}
        />
      )}
      <Tooltip
        referenceElement={containerRef}
        label={task.name}
        delay={tooltipDelay}
      />
    </>
  );
};
